<script setup>
import {ref, computed, onMounted, onBeforeUnmount} from 'vue';
import {useRouter, useRoute} from 'vue-router';
import {useAuthStore} from '@/stores/use-auth-store';
import {useDashboardStore} from '@/modules/dashboard/stores/use-dashboard-store';
import {useCommissionStore} from '@/modules/commission/stores/use-commission-store';
import {allRoles} from '@/utils/roles';

const ROLE_LABELS = Object.fromEntries(allRoles().map(r => [r.value, r.label]));

const router = useRouter();
const route = useRoute();
const auth = useAuthStore();
const dashStore = useDashboardStore();
const commissionStore = useCommissionStore();

const drawerOpen = ref(false);
const pageTitle = ref('Home');
const isOnline = ref(navigator.onLine);
const openFlagCount = computed(() => {
    if (!auth.isFieldRole) return 0;
    return (dashStore.assignments || []).reduce((n, a) => n + (a.open_flags?.length || 0), 0);
});
const toast = ref(null);
const toastTimer = ref(null);

const user = computed(() => auth.user);
const isOpsRole = computed(() => auth.isOpsRole);
const roleLabel = computed(() => {
    for (const r of (user.value?.roles || [])) {
        if (ROLE_LABELS[r]) return ROLE_LABELS[r];
    }
    return user.value?.roles?.[0] || '—';
});
const canGoBack = computed(() => window.history.length > 1 && route.name !== 'home');

const balance = computed(() => {
    if (!auth.isFieldRole) return null;
    const d = commissionStore.data;
    if (!d) return null;
    return (d.past_weeks.total || 0) + (d.banked.total || 0);
});
const currency = computed(() => commissionStore.data?.currency || 'KES');

function setTitle(title) {
    pageTitle.value = title;
}

function navTo(name) {
    drawerOpen.value = false;
    router.push({name});
}

function goBack() {
    router.back();
}

async function handleLogout() {
    drawerOpen.value = false;
    await auth.logout();
    router.replace({name: 'login'});
}

function showToast(msg) {
    toast.value = msg;
    clearTimeout(toastTimer.value);
    toastTimer.value = setTimeout(() => {
        toast.value = null;
    }, 2800);
}

function onOnline() {
    isOnline.value = true;
    showToast('Back online — syncing…');
}

function onOffline() {
    isOnline.value = false;
}

async function onSessionExpired() {
    await auth.handleSessionExpired();
    router.replace({name: 'login'});
}

onMounted(() => {
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('session:expired', onSessionExpired);

    window.$toast = (msg) => showToast(msg);

    navigator.serviceWorker?.addEventListener('message', (e) => {
        if (e.data?.type === 'QUEUE_FLUSHED' && e.data.remaining === 0) {
            showToast('All offline submissions synced.');
        }
    });

    if (auth.isFieldRole && !commissionStore.data && !commissionStore.loading) {
        commissionStore.boot();
    }
});

onBeforeUnmount(() => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    window.removeEventListener('session:expired', onSessionExpired);
    window.$toast = null;
});
</script>

<template>
    <div id="app-shell">

        <!-- Offline banner -->
        <div v-if="!isOnline" class="offline-banner">
            <span>📶</span>
            <span>You're offline — your work saves on this device and will sync when you reconnect.</span>
        </div>

        <!-- Top bar -->
        <header class="topbar">
            <button
                class="topbar-back"
                :class="{ visible: canGoBack }"
                aria-label="Go back"
                @click="goBack"
            >‹</button>

            <span class="topbar-title">{{ pageTitle }}</span>

            <div class="topbar-right">
                <span v-if="openFlagCount" class="flag-badge">{{ openFlagCount }} ⚠</span>
                <button
                    class="menu-btn"
                    aria-label="Open menu"
                    @click="drawerOpen = true"
                >
                    <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.2"
                        stroke-linecap="round"
                    >
                        <line x1="3" y1="6" x2="21" y2="6"/>
                        <line x1="3" y1="12" x2="21" y2="12"/>
                        <line x1="3" y1="18" x2="21" y2="18"/>
                    </svg>
                </button>
            </div>
        </header>

        <!-- Page content -->
        <main class="content">
            <router-view v-slot="{ Component }">
                <transition name="page" mode="out-in">
                    <component
                        :is="Component"
                        @set-title="setTitle"
                    />
                </transition>
            </router-view>
        </main>

        <!-- Drawer overlay -->
        <transition name="fade">
            <div
                v-if="drawerOpen"
                class="drawer-overlay"
                @click="drawerOpen = false"
            />
        </transition>

        <!-- Drawer -->
        <transition name="slide-right">
            <aside v-if="drawerOpen" class="drawer">

                <div class="drawer-head">
                    <div class="drawer-name">{{ user?.name || '—' }}</div>
                    <div class="drawer-role">{{ roleLabel }}</div>
                    <div v-if="balance !== null" class="drawer-balance">
                        <span class="drawer-balance-label">Commission balance</span>
                        <span class="drawer-balance-val">{{ currency }} {{ balance.toLocaleString() }}</span>
                    </div>
                </div>

                <nav class="drawer-nav">

                    <div class="drawer-section">Main</div>
                    <button
                        class="nav-btn"
                        :class="{ active: route.name === 'home' }"
                        @click="navTo('home')"
                    >
                        <span class="nav-btn-ico">🏠</span> Home
                    </button>
                    <button
                        v-if="auth.isFieldRole"
                        class="nav-btn"
                        :class="{ active: route.name === 'flags' }"
                        @click="navTo('flags')"
                    >
                        <span class="nav-btn-ico">📋</span> Your data
                    </button>
                    <button
                        v-if="!auth.isAdmin"
                        class="nav-btn"
                        :class="{ active: route.name === 'pay' }"
                        @click="navTo('pay')"
                    >
                        <span class="nav-btn-ico">💰</span> Your pay
                    </button>

                    <template v-if="isOpsRole">
                        <div class="drawer-section">Ops</div>
                        <button
                            class="nav-btn"
                            :class="{ active: route.name === 'ops-collectors' }"
                            @click="navTo('ops-collectors')"
                        >
                            <span class="nav-btn-ico">👥</span> Collectors
                        </button>
                        <button
                            class="nav-btn"
                            :class="{ active: route.name === 'ops-markets' }"
                            @click="navTo('ops-markets')"
                        >
                            <span class="nav-btn-ico">🗺</span> Markets
                        </button>
                        <button
                            class="nav-btn"
                            :class="{ active: route.name === 'ops-windows' }"
                            @click="navTo('ops-windows')"
                        >
                            <span class="nav-btn-ico">✅</span> Review queue
                        </button>
                        <button
                            class="nav-btn"
                            :class="{ active: route.name === 'ops-flags' }"
                            @click="navTo('ops-flags')"
                        >
                            <span class="nav-btn-ico">🚩</span> Flags
                        </button>
                        <button
                            class="nav-btn"
                            :class="{ active: route.name === 'market-output-menu' }"
                            @click="navTo('market-output-menu')"
                        >
                            <span class="nav-btn-ico">📄</span> Market output
                        </button>
                    </template>

                    <div class="drawer-section">More</div>
                    <button
                        class="nav-btn"
                        :class="{ active: route.name === 'help' }"
                        @click="navTo('help')"
                    >
                        <span class="nav-btn-ico">❓</span> Help guide
                    </button>
                    <button
                        class="nav-btn"
                        :class="{ active: route.name === 'report-issue' }"
                        @click="navTo('report-issue')"
                    >
                        <span class="nav-btn-ico">🔔</span> Report issue
                    </button>

                </nav>

                <div class="drawer-foot">
                    <button class="btn-logout" @click="handleLogout">Sign out</button>
                </div>

            </aside>
        </transition>

        <!-- Toast -->
        <transition name="fade">
            <div v-if="toast" class="mv-toast">{{ toast }}</div>
        </transition>

    </div>
</template>

<style scoped>
.page-enter-active,
.page-leave-active { transition: opacity .15s ease; }
.page-enter-from,
.page-leave-to { opacity: 0; }

.fade-enter-active,
.fade-leave-active { transition: opacity .2s ease; }
.fade-enter-from,
.fade-leave-to { opacity: 0; }

.slide-right-enter-active,
.slide-right-leave-active { transition: transform .25s ease; }
.slide-right-enter-from,
.slide-right-leave-to { transform: translateX(100%); }
</style>
